import { EndpointService, type APIPaginatedResponse } from "@/types/api/api";
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import type { AxiosResponse } from "axios";
import { buildQueryKey } from "@/utils/query";
import type { IListRedeemablesParams } from "./Redeemables";

/**
 * Interface representing a code generation batch request record.
 * @interface IBatchRequest
 * @property {number} quantity: Number of codes requested in the batch.
 * @property {number} status: Batch status (0 pending, 1 processing, 2 completed, 3 failed).
 * @property {string | null} [status_message]: Human-readable failure reason; null unless the batch failed.
 * @property {number} length: Total length of each generated code.
 * @property {string} created_at: ISO timestamp the batch was created.
 * @property {number} id: Batch request identifier.
 * @property {string} prefix: Prefix applied to each generated code.
 * @property {string} updated_at: ISO timestamp the batch was last updated.
 * @property {number} configuration_id: Owning redeemable configuration.
 * @property {Object} batch_metadata: Progress metadata; not used for failure detection.
 * @property {number} batch_metadata.total_batches: Number of processing chunks.
 * @property {number} batch_metadata.codes_generated: Codes generated so far.
 */
export interface IBatchRequest {
  quantity: number;
  status: number;
  status_message?: string | null;
  length: number;
  created_at: string;
  id: number;
  prefix: string;
  updated_at: string;
  configuration_id: number;
  batch_metadata: {
    total_batches: number;
    codes_generated: number;
  };
}

export interface IListBatchParams {
  page?: number;
  page_size?: number;
  configuration_id?: number;
  refetchInterval?: number;
  order_by?: string;
}

export interface ICreateBatchRequestParams {
  length: number;
  prefix: string;
  quantity: number;
  configuration_id: number;
  send_email?: boolean;
}

export interface ICreateCsvGenerationParams {
  csv: string;
  configuration_id: number;
  send_email?: boolean;
}

export interface IListBatchReponse
  extends APIPaginatedResponse<IBatchRequest> {}

export interface ICodeGenerationService {
  list: (params: IListBatchParams) => UseQueryResult<IListBatchReponse, Error>;
  create: (
    listParams: IListRedeemablesParams,
  ) => UseMutationResult<IBatchRequest, Error, ICreateBatchRequestParams>;
  createCsvGeneration: (
    listParams: IListRedeemablesParams,
  ) => UseMutationResult<IBatchRequest, Error, ICreateCsvGenerationParams>;
}

export class CodeGenerationService implements ICodeGenerationService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(params: IListBatchParams): UseQueryResult<IListBatchReponse, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "code-generation", "list"],
        venueID: this.api.selectedVenue,
        params: params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IListBatchReponse>>(
          "/promotions/batch_requests/",
          {
            params,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return {
          results: data.results,
          next: data.next,
          count: data.count,
          previous: data.previous,
        };
      },
      refetchInterval: params.refetchInterval,
    });
  }

  create(
    listParams: IListRedeemablesParams,
  ): UseMutationResult<IBatchRequest, Error, ICreateBatchRequestParams> {
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams).filter(
        ([, value]) => value !== undefined && value !== null,
      ),
    );
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "code-generation", "create"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<IBatchRequest>>(
          "/promotions/batch_requests/",
          {
            params,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "code-generation", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
      },
    });
  }

  createCsvGeneration(
    listParams: IListRedeemablesParams,
  ): UseMutationResult<IBatchRequest, Error, ICreateCsvGenerationParams> {
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams).filter(
        ([, value]) => value !== undefined && value !== null,
      ),
    );
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "code-generation", "create-csv-generation"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<IBatchRequest>>(
          "/promotions/batch_requests/csv",
          {
            params,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "code-generation", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
      },
    });
  }
}
